import React from 'react';
import { useSelector } from 'react-redux';
import { hooks } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { featureSelectors } from '@lifesize/nucleus';
import { Box, makeStyles, Theme } from '@material-ui/core';
import DialPadComponent from 'components/DialPad/DialPad';
import CallDuration from 'components/InCall/CallDuration';
import EndCallButton from 'components/CallControls/EndCallButton';
import LayoutSwitch from 'components/CallControls/LayoutSwitch';
import LiveCaptionsButton from 'components/CallControls/LiveCaptionsButton';
import MoreButton from 'components/CallControls/MoreButton';
import Pip from 'components/CallControls/Pip';
import PresentationButton from 'components/CallControls/PresentationButton';
import RaiseHandButton from 'components/CallControls/RaiseHandButton';
import RecordingButton from 'components/CallControls/RecordingButton';
import VideoButton from 'components/CallControls/VideoButton';
import { FLAG_SFU_COMPOSITE_SWITCH } from 'constants/FeatureFlags';
import FarEndCameraControls from 'containers/FarEndCameraControls/FarEndCameraControlsContainer';
import useViewport from 'hooks/useViewport';
import AudioButtonWithListener from '../CallControls/AudioButton/AudioButtonWithListener';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import { FLAG_SPEAKING_WHILE_MUTED } from '../../constants/FeatureFlags';
import AudioButton from '../CallControls/AudioButton/AudioButton';
import { isMobile } from 'utils/browser-utils';
import { getMaximumReceiveStreams } from 'utils/mediaConstraints';
import clsx from 'clsx';

export const footerHeight = 72;
const pipHeight = footerHeight - 16; // allow for theme.spacing(1) on top and bottom

interface Props {
  intl: { formatMessage: Function };
  onDTMFClicked: Function;
  showCameraControls: boolean;
  showDialPad: boolean;
  startTime: number;
  toggleDialPad: Function;
  isMinimized: boolean;
}

const CallFooter = (props: Props) => {
  const { showCameraControls, startTime, toggleDialPad, isMinimized, showDialPad, onDTMFClicked } = props;
  const isSpeakingWhileMutedFeatureEnabled = useFeatureFlag(FLAG_SPEAKING_WHILE_MUTED);
  const { layout } = hooks.useMedia();
  const { sfuSettings } = hooks.useAccountSettings();
  const { isFooterCompact, isFooterFull, isMobileView } = useViewport();
  const showMobileView = isMobileView || isMobile();
  const isSfuMultiStream = getMaximumReceiveStreams() > 1 && !(sfuSettings?.sfuMultiStream === false);
  const isMultiStreamLayout = isSfuMultiStream && !isMobile();
  const compositeLayoutSwitchEnabled = useSelector(
    (state) => !!featureSelectors.getFeatureFlag(state)(FLAG_SFU_COMPOSITE_SWITCH)
  );
  const showLayoutSwitch = !showMobileView && (isMultiStreamLayout || (compositeLayoutSwitchEnabled && !isMobile()));
  const classes = useStyles();

  if (isMinimized) {
    return (
      <Box display="flex">
        <div className={clsx(classes.center, classes.minimizedFooter)}>
          <AudioButton useLiteButton />
          <EndCallButton />
        </div>
        <div className={classes.right}>
          <CallDuration startTime={startTime} />
        </div>
      </Box>
    );
  }
  return (
    <div className={classes.container}>
      {isFooterCompact ? (
        <div className={classes.center}>
          <AudioButton />
          <VideoButton />
          <RaiseHandButton />
          <EndCallButton />
          <MoreButton isCompact={true} />
          {showCameraControls && <FarEndCameraControls />}
          <DialPadComponent isVisible={showDialPad} handleClose={toggleDialPad} addNumber={onDTMFClicked} />
        </div>
      ) : (
        <>
          <div className={classes.left}>
            <Pip height={pipHeight} />
          </div>
          <div className={classes.center}>
            {showLayoutSwitch && <LayoutSwitch className={classes.layoutSwitch} value={layout} />}
            {/*TODO - Remove feature flag when fully deployed to prod */}
            {isSpeakingWhileMutedFeatureEnabled && <AudioButtonWithListener />}
            {!isSpeakingWhileMutedFeatureEnabled && <AudioButton />}
            <VideoButton />
            <div className={classes.separator} />
            {!isMobile() && <PresentationButton />}
            <RecordingButton />
            <RaiseHandButton />
            <LiveCaptionsButton />
          </div>
          <div className={classes.right}>
            {isFooterFull && <CallDuration startTime={startTime} />}
            <EndCallButton />
            <MoreButton />
            {showCameraControls && <FarEndCameraControls />}
            <DialPadComponent isVisible={showDialPad} handleClose={toggleDialPad} addNumber={onDTMFClicked} />
          </div>
        </>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    alignItems: 'stretch',
    color: colors.white,
    display: 'flex',
    flex: `0 0 ${footerHeight}px`,
    justifyContent: 'center',
    zIndex: 11
  },
  left: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 0px',
    justifyContent: 'flex-start',
    position: 'relative'
  },
  center: {
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'center',
    position: 'relative',
    '& > *': {
      margin: `0 ${theme.spacing(0.5)}px`
    }
  },
  right: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 0px',
    justifyContent: 'flex-end',
    padding: `0 ${theme.spacing(1)}px 0 0`,
    position: 'relative',
    '& > *': {
      margin: `0 ${theme.spacing(0.5)}px`
    }
  },
  separator: {
    borderLeft: `1px solid ${colors.concrete}`,
    height: 48,
    margin: `0 ${theme.spacing(0.5)}px`
  },
  layoutSwitch: {
    fontSize: '12px !important' // the height of the switch is 4em; use this font size to control the size of the entire control
  },
  minimizedFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: '1 1 70px',
    '& + div': {
      color: colors.white,
      fontSize: '0.9rem'
    },
    '& > span': {
      padding: theme.spacing(1, 0)
    },
    '& button': {
      width: 35,
      height: 35,
      '& svg': {
        height: 25
      }
    }
  }
}));

export default CallFooter;
